// Admin statistics: stat tiles, per-category table, per-day bars for uploads and
// downloads. Single series per chart → one hue (--chart-bar, validated ≥3:1 on
// white), no legend, values in ink tokens; the tables alongside are the
// always-readable fallback.
import React, { useEffect, useMemo, useState } from 'react';
import { api, thumbUrl } from '../api.js';
import { fmtBytes } from '../text.js';
import { toast } from '../components/toast.jsx';

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

// Densify sparse {date, count, …} rows into one entry per calendar day.
function lastNDays(rows) {
	const byDate = new Map(rows.map((d) => [d.date, d]));
	const days = [];
	const today = new Date();
	for (let i = WINDOW_DAYS - 1; i >= 0; i--) {
		const date = new Date(today.getTime() - i * DAY_MS).toISOString().slice(0, 10);
		days.push({ count: 0, ...byDate.get(date), date });
	}
	return days;
}

function DayChart({ ariaLabel, days }) {
	const maxDay = Math.max(1, ...days.map((d) => d.count));
	return (
		<div className='chart' role='img' aria-label={`${ariaLabel}, Maximum ${maxDay}`}>
			<div className='chart__bars'>
				{days.map((d) => (
					<div className='chart__slot' key={d.date} title={d.title ?? `${d.date}: ${d.count}`}>
						{d.count === maxDay && d.count > 0 && <span className='chart__toplabel'>{d.count}</span>}
						<div className='chart__bar' style={{ height: `${Math.round((d.count / maxDay) * 100)}%` }} />
					</div>
				))}
			</div>
			<div className='chart__axis'>
				{days.map((d, i) => (
					<span className='chart__tick' key={d.date}>
						{i % 7 === 0 || i === days.length - 1 ? d.date.slice(8) + '.' + d.date.slice(5, 7) + '.' : ''}
					</span>
				))}
			</div>
		</div>
	);
}

export default function Stats() {
	const [stats, setStats] = useState(null);

	useEffect(() => {
		api.stats()
			.then(setStats)
			.catch(() => toast('Statistik konnte nicht geladen werden.', 'error'));
	}, []);

	const uploadDays = useMemo(() => (stats ? lastNDays(stats.uploadsByDay) : []), [stats]);
	const downloadDays = useMemo(() => {
		if (!stats) return [];
		const rows = stats.downloads.byDay.map((d) => ({ date: d.date, count: d.files, events: d.events }));
		return lastNDays(rows).map((d) => ({
			...d,
			title: `${d.date}: ${d.count} Dateien (${d.events ?? 0} Vorgänge)`,
		}));
	}, [stats]);

	if (!stats) return <div className='home__loading'>Wird geladen …</div>;

	const totals = stats.categories.reduce(
		(acc, c) => ({ count: acc.count + c.count, bytes: acc.bytes + c.bytes, failed: acc.failed + c.failed }),
		{ count: 0, bytes: 0, failed: 0 },
	);
	const diskLow = stats.disk && stats.disk.freeBytes < 10 * 1024 * 1024 * 1024;

	return (
		<section className='stats'>
			<div className='stats__tiles'>
				<div className='stattile'>
					<div className='stattile__value'>{totals.count}</div>
					<div className='stattile__label'>Medien gesamt</div>
				</div>
				<div className='stattile'>
					<div className='stattile__value'>{stats.downloads.total.files}</div>
					<div className='stattile__label'>Downloads gesamt</div>
				</div>
				<div className='stattile'>
					<div className='stattile__value'>{fmtBytes(totals.bytes)}</div>
					<div className='stattile__label'>Speicher belegt</div>
				</div>
				<div className={`stattile ${diskLow ? 'stattile--warn' : ''}`}>
					<div className='stattile__value'>{stats.disk ? fmtBytes(stats.disk.freeBytes) : '—'}</div>
					<div className='stattile__label'>{diskLow ? '⚠ Freier Speicher' : 'Freier Speicher'}</div>
				</div>
				<div className='stattile'>
					<div className='stattile__value'>{stats.queue.pending}</div>
					<div className='stattile__label'>Videos in Warteschlange</div>
				</div>
			</div>

			<h2 className='stats__heading'>Uploads – letzte 30 Tage</h2>
			<DayChart ariaLabel='Uploads pro Tag der letzten 30 Tage' days={uploadDays} />

			<h2 className='stats__heading'>Downloads – letzte 30 Tage</h2>
			<DayChart ariaLabel='Heruntergeladene Dateien pro Tag der letzten 30 Tage' days={downloadDays} />
			<p className='stats__byuploader'>
				Insgesamt <strong>{stats.downloads.total.files}</strong> Dateien in{' '}
				<strong>{stats.downloads.total.events}</strong> Download-Vorgängen (ein ZIP zählt als ein Vorgang).
			</p>

			<h2 className='stats__heading'>Top-Downloads</h2>
			{stats.downloads.top.length === 0 ? (
				<p className='stats__byuploader'>Noch keine Downloads.</p>
			) : (
				<div className='stats__tablewrap'>
					<table className='stats__table'>
						<thead>
							<tr>
								<th>Datei</th>
								<th>Kategorie</th>
								<th>Downloads</th>
							</tr>
						</thead>
						<tbody>
							{stats.downloads.top.map((t) => (
								<tr key={t.id}>
									<td>
										<img
											className='stats__topthumb'
											src={thumbUrl(t)}
											alt=''
											loading='lazy'
											onError={(e) => {
												e.currentTarget.style.visibility = 'hidden';
											}}
										/>
										<span className='stats__topname' title={t.name}>
											{t.name}
										</span>
									</td>
									<td>{t.category}</td>
									<td>{t.count}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<h2 className='stats__heading'>Kategorien</h2>
			<div className='stats__tablewrap'>
				<table className='stats__table'>
					<thead>
						<tr>
							<th>Kategorie</th>
							<th>Medien</th>
							<th>Fotos</th>
							<th>Videos</th>
							<th>In Arbeit</th>
							<th>Fehler</th>
							<th>Größe</th>
						</tr>
					</thead>
					<tbody>
						{stats.categories.map((c) => (
							<tr key={c.id}>
								<td>{c.name}</td>
								<td>{c.count}</td>
								<td>{c.images}</td>
								<td>{c.videos}</td>
								<td>{c.processing}</td>
								<td>{c.failed}</td>
								<td>{fmtBytes(c.bytes)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<h2 className='stats__heading'>Herkunft</h2>
			<p className='stats__byuploader'>
				Gäste: <strong>{stats.byUploader.guest}</strong> · Admin: <strong>{stats.byUploader.admin}</strong> · Import:{' '}
				<strong>{stats.byUploader.import}</strong>
			</p>
		</section>
	);
}
